import type { AcCardStatus, AccreditationSummary, MeResponse } from "@seagames/contracts";
import { useUiStrings } from "@seagames/ui";
import { Link } from "@tanstack/react-router";
import { type ComponentType, type ReactNode, type SVGProps, useState } from "react";
import { useTranslation } from "react-i18next";
import { CategoryBanner } from "../access-codes/category-banner";
import { canRegister, hasHostRights } from "../accreditation/model";
import {
	DownIcon,
	EditIcon,
	GlobeIcon,
	InboxIcon,
	InfoIcon,
	LayersIcon,
	ListIcon,
	NoPhotoIcon,
	RightIcon,
	ShortlistIcon,
	UserPlusIcon,
	UsersIcon,
	WarnIcon,
} from "./icons";
import { useMedia } from "./use-media";
import { type DashboardSearch, pct, recordsSearch, statusCounts } from "./model";

type Glyph = ComponentType<SVGProps<SVGSVGElement>>;

/** Her card head (`.card__h.dh`): a muted 30px icon, a navy title, a muted line under it. */
function CardHead({ icon: Icon, title, sub }: { icon: Glyph; title: string; sub?: string }) {
	return (
		<div className="db-card__h">
			<span className="db-card__ic">
				<Icon />
			</span>
			<h2>
				{title}
				{sub && <small>{sub}</small>}
			</h2>
		</div>
	);
}

/** Skeleton rows in a list's shape while the summary loads. */
function SkeletonRows({ n, h = "h-[1.5rem]" }: { n: number; h?: string }) {
	return (
		<div className="grid gap-[1rem] py-[0.5rem]">
			{Array.from({ length: n }, (_, i) => (
				<span key={i} className={`skeleton block w-full ${h}`} />
			))}
		</div>
	);
}

/** Her summary order first (approved → returned), then the two closing states. */
const SUMMARY_ORDER: readonly AcCardStatus[] = ["APPROVED", "SUBMITTED", "PENDING", "DRAFT", "RETURN", "REJECTED", "WITHDRAWN"];

/**
 * Her "Games bodies summary" card, for every record in view: one row per
 * status (coloured dot, uppercase label, big coloured number — the records
 * page's status colours) and the approval donut: approved of the records
 * that still stand (rejected and withdrawn left out, as hers).
 */
export function StatusSummaryCard({ summary, search }: { summary: AccreditationSummary | undefined; search: DashboardSearch }) {
	const { t } = useTranslation("dashboard");
	const { t: tAcc } = useTranslation("accreditation");
	const { formatNumber: fmt } = useUiStrings();
	const by = statusCounts(summary);
	const active = (summary?.total ?? 0) - by.REJECTED - by.WITHDRAWN;
	const p = pct(by.APPROVED, active);
	const R = 70;
	const C = 2 * Math.PI * R;
	const len = (C * p) / 100;

	return (
		<section className="db-card">
			<CardHead icon={UsersIcon} title={t("status.title")} sub={t("status.subtitle")} />
			{summary ? (
				<>
					<div className="db-sm">
						{SUMMARY_ORDER.map((s) => (
							<Link key={s} to="/accreditation/records" search={recordsSearch(search, { status: s })} data-ac-status={s}>
								<span className="db-sm__l">
									<i />
									{tAcc(`record.status.${s}`)}
								</span>
								<span className="db-sm__n" data-ac-status={s}>
									{fmt(by[s])}
								</span>
							</Link>
						))}
					</div>
					<div
						className="db-dn"
						role="img"
						aria-label={t("status.progressAria", { approved: fmt(by.APPROVED), active: fmt(active), percent: fmt(p) })}
					>
						<svg viewBox="0 0 180 180" aria-hidden>
							<circle cx="90" cy="90" r={R} fill="none" stroke="var(--color-gms-navy-soft)" strokeWidth="22" />
							{len > 0 && (
								<circle
									cx="90"
									cy="90"
									r={R}
									fill="none"
									stroke="var(--color-gms-navy)"
									strokeWidth="22"
									strokeLinecap="round"
									strokeDasharray={`${len.toFixed(1)} ${C.toFixed(1)}`}
									transform="rotate(-90 90 90)"
								/>
							)}
						</svg>
						<span className="db-dn__t">
							<span>{t("status.progress")}</span>
							<b>
								{fmt(by.APPROVED)} / {fmt(active)}
							</b>
							<small>{fmt(p)}%</small>
						</span>
					</div>
				</>
			) : (
				<SkeletonRows n={7} />
			)}
		</section>
	);
}

/**
 * Meter list (her `.mtr`): a code tag, the name, the count, a bar against
 * the largest row. Six rows (twelve in two columns on wide screens), then
 * "Show all".
 */
function MeterCard({
	icon,
	title,
	sub,
	rows,
	loading,
	twoColumns = false,
}: {
	icon: Glyph;
	title: string;
	sub: string;
	rows: Array<{ key: string; tag: ReactNode; name: string; count: number; to: ReturnType<typeof recordsSearch> }>;
	loading: boolean;
	twoColumns?: boolean;
}) {
	const { t } = useTranslation("dashboard");
	const { formatNumber: fmt } = useUiStrings();
	const [all, setAll] = useState(false);
	const max = Math.max(1, ...rows.map((r) => r.count));
	const wide = useMedia("(min-width: 80rem)");
	const clipAt = twoColumns && wide ? 12 : 6;

	return (
		<section className="db-card">
			<CardHead icon={icon} title={title} sub={sub} />
			{loading ? (
				<SkeletonRows n={6} h="h-[2.25rem]" />
			) : rows.length === 0 ? (
				<p className="text-[0.8125rem] text-gms-ink-3">{t("empty")}</p>
			) : (
				<>
					<div className={`db-mtr ${twoColumns ? "db-mtr--2" : ""}`}>
						{(all ? rows : rows.slice(0, clipAt)).map((r) => (
							<Link key={r.key} to="/accreditation/records" search={r.to}>
								{r.tag}
								<span className="db-mtr__n" title={r.name}>
									{r.name}
								</span>
								<span className="db-mtr__v">{fmt(r.count)}</span>
								<span className="db-mtr__b" aria-hidden>
									<i style={{ width: `${(r.count / max) * 100}%` }} />
								</span>
							</Link>
						))}
					</div>
					{rows.length > clipAt && (
						<button type="button" className="db-clip cursor-pointer hover:underline" aria-expanded={all} onClick={() => setAll((v) => !v)}>
							{all ? t("orgs.showFewer") : t("orgs.showAll", { count: rows.length })}
							<DownIcon />
						</button>
					)}
				</>
			)}
		</section>
	);
}

/** Her "Approval by contingent", in what the summary carries: records per organisation, largest first. */
export function OrganisationsCard({ summary, search }: { summary: AccreditationSummary | undefined; search: DashboardSearch }) {
	const { t } = useTranslation("dashboard");
	const rows = [...(summary?.by_organization ?? [])]
		.sort((a, b) => b.count - a.count || a.responsible_organization.name.localeCompare(b.responsible_organization.name))
		.map((o) => ({
			key: o.responsible_organization.id,
			tag: <span className="db-mtr__c">{o.responsible_organization.code ?? "GMS"}</span>,
			name: o.responsible_organization.name,
			count: o.count,
			to: recordsSearch(search, { responsible_organization_id: o.responsible_organization.id }),
		}));
	return <MeterCard icon={GlobeIcon} title={t("orgs.title")} sub={t("notCounted")} rows={rows} loading={!summary} twoColumns />;
}

/** Records per category (main function), largest first, on the category's own card colour. */
export function CategoriesCard({ summary, search }: { summary: AccreditationSummary | undefined; search: DashboardSearch }) {
	const { t } = useTranslation("dashboard");
	const rows = [...(summary?.by_main_function ?? [])]
		.sort((a, b) => b.count - a.count || a.main_function.code.localeCompare(b.main_function.code))
		.map((m) => ({
			key: m.main_function.id,
			tag: <CategoryBanner code={m.main_function.code} cardColor={m.main_function.card_color} inkColor={m.main_function.card_ink_color} />,
			name: m.main_function.name,
			count: m.count,
			to: recordsSearch(search, { main_function_id: m.main_function.id, sub_function_id: undefined }),
		}));
	return <MeterCard icon={LayersIcon} title={t("categories.title")} sub={t("notCounted")} rows={rows} loading={!summary} />;
}

/**
 * Her "Needs attention" rows that the summary can count: waiting for review
 * (a host) or returned to you (an organisation), no photo, drafts. Urgent
 * and multiple-records counts are not in the summary, so those rows are left
 * out rather than costing extra list queries.
 */
export function NeedsAttention({ summary, search, session }: { summary: AccreditationSummary | undefined; search: DashboardSearch; session: MeResponse }) {
	const { t } = useTranslation("dashboard");
	const { formatNumber: fmt } = useUiStrings();
	const by = statusCounts(summary);
	const host = hasHostRights(session);
	const rows: Array<{ icon: Glyph; tone: string; title: string; hint: string; n: number; to: ReturnType<typeof recordsSearch> }> = [
		host
			? { icon: InboxIcon, tone: "", title: t("attention.review"), hint: t("attention.reviewHint"), n: by.SUBMITTED, to: recordsSearch(search, { status: "SUBMITTED" }) }
			: { icon: WarnIcon, tone: "db-att__ic--w", title: t("attention.returned"), hint: t("attention.returnedHint"), n: by.RETURN, to: recordsSearch(search, { status: "RETURN" }) },
		{
			icon: NoPhotoIcon,
			tone: "db-att__ic--w",
			title: t("attention.noPhoto"),
			hint: t("attention.noPhotoHint"),
			n: summary?.no_photo ?? 0,
			to: recordsSearch(search, { no_photo: true }),
		},
		{ icon: EditIcon, tone: "", title: t("attention.drafts"), hint: t("attention.draftsHint"), n: by.DRAFT, to: recordsSearch(search, { status: "DRAFT" }) },
	];
	return (
		<section className="db-card db-card--flush">
			<CardHead icon={InfoIcon} title={t("attention.title")} />
			<div className="db-att">
				{rows.map(({ icon: Icon, ...r }) => (
					<Link key={r.title} to="/accreditation/records" search={r.to}>
						<span className={`db-att__ic ${r.tone}`}>
							<Icon />
						</span>
						<span className="db-att__t">
							<b>{r.title}</b>
							<span>{r.hint}</span>
						</span>
						{summary ? <span className="db-att__n">{fmt(r.n)}</span> : <span className="skeleton block h-[1.5rem] w-[2rem]" />}
						<RightIcon />
					</Link>
				))}
			</div>
		</section>
	);
}

/** Her quick actions; "Register new record" only for a login that may register. */
export function QuickActions({ session }: { session: MeResponse }) {
	const { t } = useTranslation("dashboard");
	return (
		<section>
			<h2 className="db-qa__h">{t("quick.title")}</h2>
			<div className="db-qa">
				{canRegister(session) && (
					<Link to="/accreditation/add" className="is-red">
						<UserPlusIcon />
						{t("quick.register")}
					</Link>
				)}
				<Link to="/accreditation/shortlist">
					<ShortlistIcon />
					{t("quick.shortlist")}
				</Link>
				<Link to="/accreditation/records">
					<ListIcon />
					{t("quick.records")}
				</Link>
				<Link to="/accreditation/records" search={{ status: "DRAFT" }}>
					<EditIcon />
					{t("quick.drafts")}
				</Link>
			</div>
		</section>
	);
}
